import { useEffect, useState } from "react";
import { api } from "./api";
import { COUNTRIES } from "./countries";

/**
 * Resolve the visitor's country ISO-2 code with progressively weaker
 * heuristics. Called once per page:
 *
 *  1. `GET /api/geo/detect` — IP-based (Cloudflare / edge header, else ipapi.co)
 *  2. Fallback: `initial` (default `IN`)
 *
 * Only returns a code we actually support (present in `/lib/countries.js`).
 * Backend call has a 3-s timeout via axios default so the widget never
 * hangs. `initial` lets callers pre-seed (e.g. from user.country).
 *
 * ⚠ We intentionally do NOT use `navigator.language` — most Indian users
 * keep their phones/browsers set to `en-US`, which was flipping the
 * booking-form country from India → US and confusing patients. IP-based
 * detection is far more accurate for our real audience.
 */
export function useCountryAutoDetect(initial = "IN") {
  const [country, setCountry] = useState(initial);
  const [source, setSource] = useState("initial");

  useEffect(() => {
    let cancelled = false;

    // Only trust IP-based detection. If it fails, we stay on `initial` (IN).
    (async () => {
      try {
        const r = await api.get("/geo/detect", { timeout: 3000 });
        if (cancelled) return;
        const cc = (r.data?.country_code || "").toUpperCase();
        if (cc && COUNTRIES.find((c) => c.iso === cc)) {
          setCountry(cc);
          setSource(r.data?.source || "ip");
        }
      } catch (e) { /* silent — keep initial */ }
    })();

    return () => { cancelled = true; };
  }, []); // fire once on mount

  return { country, setCountry, source };
}
